// CareLogix ORACLE™ CEO Travel Command™ — AdminOne page (additive).
// Dark green / gold palette · responsive cards · truthful status only.
// No fabricated fares: when provider keys are absent the page surfaces
// AWAITING_TRAVEL_PROVIDER_KEY / NO_LIVE_FARE_DATA_AVAILABLE.
import React, { useCallback, useEffect, useMemo, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function tokenHeader() {
  let token = "";
  try { token = localStorage.getItem("carelogix_adminone_token") || ""; } catch (_e) { /* ignore */ }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api(path, opts = {}) {
  const res = await fetch(`${BACKEND}/api/adminone/ceo-travel-command${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...tokenHeader(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${txt.slice(0, 200)}`);
  }
  return res.json();
}

const PALETTE = {
  bg:        "#06120c",
  panelBg:   "rgba(13, 36, 22, 0.65)",
  panelLine: "rgba(122, 200, 96, 0.22)",
  gold:      "#c5a059",
  goldDim:   "rgba(197, 160, 89, 0.55)",
  green:     "#7AC860",
  greenDim:  "rgba(122, 200, 96, 0.65)",
  ink:       "#dfe3eb",
  inkDim:    "rgba(223, 227, 235, 0.62)",
  warn:      "#E5A23A",
  alert:     "#E5484D",
};

const styles = {
  page: { minHeight: "100vh", background: PALETTE.bg, color: PALETTE.ink, padding: "32px 5vw 80px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 24, borderBottom: `1px solid ${PALETTE.panelLine}`, paddingBottom: 16 },
  h1: { fontSize: "clamp(28px, 3.4vw, 40px)", fontWeight: 700, color: PALETTE.gold, letterSpacing: "0.02em", margin: 0 },
  h2: { fontSize: "16px", fontWeight: 600, color: PALETTE.green, textTransform: "uppercase", letterSpacing: "0.18em", margin: 0 },
  sub: { color: PALETTE.inkDim, fontSize: 14 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginTop: 20 },
  card: { background: PALETTE.panelBg, border: `1px solid ${PALETTE.panelLine}`, borderRadius: 14, padding: 20, boxShadow: "0 12px 40px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: 12 },
  cardTitle: { fontSize: 13, fontWeight: 700, color: PALETTE.gold, letterSpacing: "0.16em", textTransform: "uppercase", margin: 0 },
  cardLine: { color: PALETTE.ink, fontSize: 14, lineHeight: 1.55 },
  cardDim:  { color: PALETTE.inkDim, fontSize: 12.5, lineHeight: 1.55 },
  badge:    (tone = "default") => ({
    display: "inline-block",
    padding: "3px 10px",
    border: `1px solid ${tone === "alert" ? PALETTE.alert : tone === "warn" ? PALETTE.warn : tone === "ok" ? PALETTE.green : PALETTE.goldDim}`,
    color:   tone === "alert" ? PALETTE.alert : tone === "warn" ? PALETTE.warn : tone === "ok" ? PALETTE.green : PALETTE.gold,
    borderRadius: 999, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600,
  }),
  btn: (primary = false) => ({
    background: primary ? PALETTE.green : "transparent",
    color:      primary ? "#06120c" : PALETTE.green,
    border:     `1px solid ${PALETTE.green}`,
    padding:    "8px 14px",
    borderRadius: 8,
    fontSize:   12.5,
    fontWeight: 600,
    letterSpacing: "0.06em",
    cursor:     "pointer",
    transition: "transform 120ms ease, background 120ms ease",
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:    { textAlign: "left", color: PALETTE.gold, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", padding: "6px 8px", borderBottom: `1px solid ${PALETTE.panelLine}` },
  td:    { color: PALETTE.ink, padding: "6px 8px", borderBottom: `1px solid rgba(122, 200, 96, 0.08)` },
  disclaimer: { marginTop: 32, padding: 16, background: "rgba(229, 162, 58, 0.08)", border: `1px solid ${PALETTE.warn}`, borderRadius: 10, color: PALETTE.ink, fontSize: 12.5, lineHeight: 1.6 },
};

function StatusBadge({ status }) {
  const tone =
    status === "ACTIVE" || status === "GOOD_VALUE" || status === "PRICE_DROP" || status === "FLEXIBLE_FARE_FOUND" ? "ok" :
    status === "WATCHING" ? "default" :
    status === "HIGH_PRICE" || status === "AWAITING_PROVIDER_KEY" ? "warn" :
    status === "NO_DATA_AVAILABLE" ? "warn" : "default";
  return <span style={styles.badge(tone)} data-testid={`ceo-travel-status-${status}`}>{status}</span>;
}

function Card({ title, children, testid }) {
  return (
    <section style={styles.card} data-testid={testid}>
      <h3 style={styles.cardTitle}>{title}</h3>
      {children}
    </section>
  );
}

export default function AdminOneCEOTravelCommand() {
  const [status,      setStatus]      = useState(null);
  const [routes,      setRoutes]      = useState([]);
  const [airlines,    setAirlines]    = useState([]);
  const [deltaRules,  setDeltaRules]  = useState(null);
  const [brief,       setBrief]       = useState(null);
  const [emailDraft,  setEmailDraft]  = useState(null);
  const [agentReply,  setAgentReply]  = useState(null);
  const [agentInput,  setAgentInput]  = useState("Build Rwanda itinerary from JFK");
  const [loading,     setLoading]     = useState(true);
  const [err,         setErr]         = useState("");
  const userId = "ceo-1";

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, r, a, dr, b, em] = await Promise.all([
        api("/status"),
        api("/routes"),
        api("/preferred-airlines"),
        api("/delta-rules"),
        api(`/briefing/${userId}`),
        api(`/email-preview/${userId}`),
      ]);
      setStatus(s);
      setRoutes(r.routes || []);
      setAirlines(a.airlines || []);
      setDeltaRules(dr.rules || null);
      setBrief(b);
      setEmailDraft(em.email || null);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const askAgent = useCallback(async (q) => {
    try {
      const data = await api("/agent/query", {
        method: "POST",
        body: JSON.stringify({ query: q, user_id: userId }),
      });
      setAgentReply(data);
    } catch (e) {
      setAgentReply({ error: String(e.message || e) });
    }
  }, [userId]);

  const quickActions = useMemo(() => ([
    { label: "Find today's best Delta fare",       q: "Find today's best Delta fare from DFW or IAH" },
    { label: "Check weekly flight sales",          q: "Check this week's flight sales DFW IAH JFK" },
    { label: "Build Nigeria itinerary",            q: "Build Nigeria (Lagos) itinerary from JFK" },
    { label: "Build Rwanda itinerary",             q: "Build Rwanda (Kigali) itinerary from JFK via SkyTeam" },
    { label: "Build Ghana itinerary",              q: "Build Ghana (Accra) itinerary from JFK" },
    { label: "Build Kenya itinerary",              q: "Build Kenya (Nairobi) itinerary from JFK" },
    { label: "Build Uganda itinerary",             q: "Build Uganda (Entebbe) itinerary from JFK" },
    { label: "Compare refundable vs non-refundable", q: "Compare refundable vs non-refundable Main Cabin Delta" },
    { label: "Check MQD impact",                   q: "Check MQD impact of upcoming itineraries" },
  ]), []);

  if (loading) {
    return (
      <div style={styles.page} data-testid="ceo-travel-command-page">
        <div style={styles.cardDim}>Loading CEO Travel Command…</div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-testid="ceo-travel-command-page">
      <header style={styles.header}>
        <h2 style={styles.h2}>AdminOne · Hardening v1 · Truthful Status</h2>
        <h1 style={styles.h1}>CareLogix ORACLE™ CEO Travel Command™</h1>
        <div style={styles.sub}>
          Dallas · Houston · New York &nbsp;↔&nbsp; Nigeria · Rwanda · Ghana · Kenya · Uganda
        </div>
        {status && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <StatusBadge status={status.provider?.status || "ACTIVE"} />
            <span style={styles.cardDim}>
              Routes tracked: {status.tracked_routes} · Preferred airlines: {status.preferred_airlines?.length || 0}
            </span>
            <button type="button" style={styles.btn(false)} onClick={loadAll} data-testid="ceo-travel-refresh">Refresh</button>
          </div>
        )}
        {err && <div style={{ color: PALETTE.alert, fontSize: 13 }} data-testid="ceo-travel-error">{err}</div>}
      </header>

      {/* ── Top widget row: status / MQD / next route ──────────────────────── */}
      <div style={styles.grid}>
        <Card title="Today's Best Route" testid="ceo-travel-todays-best">
          {brief?.todays_route_deals?.count > 0 ? (
            <>
              <div style={styles.cardLine}>{brief.todays_route_deals.items[0]?.route_label || "—"}</div>
              <div style={styles.cardDim}>Status: <StatusBadge status={brief.todays_route_deals.status} /></div>
            </>
          ) : (
            <>
              <StatusBadge status={brief?.todays_route_deals?.status || "NO_DATA_AVAILABLE"} />
              <div style={styles.cardDim}>{NO_LIVE_FARE_LABEL}</div>
            </>
          )}
        </Card>

        <Card title="This Week's Deals" testid="ceo-travel-weekly-deals">
          <StatusBadge status={brief?.todays_route_deals?.status || "NO_DATA_AVAILABLE"} />
          <div style={styles.cardDim}>
            {brief?.todays_route_deals?.count || 0} deal(s) on record · {status?.provider?.fare_data || "—"}
          </div>
        </Card>

        <Card title="Monthly MQD Progress" testid="ceo-travel-mqd-progress">
          <div style={styles.cardLine}>
            Current MQD <strong style={{ color: PALETTE.gold }}>{brief?.delta_mqd_progress?.current_mqd || 0}</strong>
          </div>
          <div style={styles.cardDim}>
            Pending {brief?.delta_mqd_progress?.pending_mqd || 0} · Target {brief?.delta_mqd_progress?.target_medallion || "PLATINUM"}
          </div>
          <StatusBadge status={brief?.delta_mqd_progress?.status || "NO_DATA_AVAILABLE"} />
        </Card>

        <Card title="Next Suggested Trip" testid="ceo-travel-next-trip">
          {brief?.upcoming_itinerary_risks?.count > 0 ? (
            <>
              <div style={styles.cardLine}>{brief.upcoming_itinerary_risks.items[0]?.trip_name || "—"}</div>
              <div style={styles.cardDim}>{brief.upcoming_itinerary_risks.items[0]?.depart_date || ""}</div>
            </>
          ) : (
            <>
              <StatusBadge status="NO_DATA_AVAILABLE" />
              <div style={styles.cardDim}>Add itineraries via the API to populate.</div>
            </>
          )}
        </Card>

        <Card title="Travel Risk Alerts" testid="ceo-travel-risk-alerts">
          {brief?.international_travel_alerts?.count > 0 ? (
            brief.international_travel_alerts.items.slice(0, 3).map((a) => (
              <div key={a._id} style={styles.cardLine}>· {a.region} — {a.headline}</div>
            ))
          ) : (
            <>
              <StatusBadge status="NO_DATA_AVAILABLE" />
              <div style={styles.cardDim}>No active visa / weather / security alerts on file.</div>
            </>
          )}
        </Card>

        <Card title="Upcoming Itineraries" testid="ceo-travel-upcoming">
          <div style={styles.cardLine}>
            {brief?.upcoming_itinerary_risks?.count || 0} itinerary item(s) on file.
          </div>
        </Card>
      </div>

      {/* ── Route Watch ──────────────────────────────────────────────────── */}
      <div style={{ marginTop: 36 }}>
        <h3 style={{ ...styles.h2, color: PALETTE.gold }}>Route Watch · 18 Lanes</h3>
        <div style={{ ...styles.card, marginTop: 12 }} data-testid="ceo-travel-route-watch">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Lane</th>
                <th style={styles.th}>Origin</th>
                <th style={styles.th}>Destination</th>
                <th style={styles.th}>Live Fare</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={`${r.origin}-${r.dest}`}>
                  <td style={styles.td}>{r.label}</td>
                  <td style={styles.td}>{r.origin}</td>
                  <td style={styles.td}>{r.dest}</td>
                  <td style={styles.td}><StatusBadge status={status?.provider?.fare_data === "LIVE" ? "ACTIVE" : "AWAITING_PROVIDER_KEY"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Loyalty / Delta Rules / Africa Travel Command ────────────────── */}
      <div style={{ ...styles.grid, marginTop: 28 }}>
        <Card title="Delta Rules Engine" testid="ceo-travel-delta-rules">
          {deltaRules && (
            <ul style={{ margin: 0, paddingLeft: 20, color: PALETTE.ink, fontSize: 13.5, lineHeight: 1.7 }}>
              <li>Avoid Basic Economy: <strong>{String(deltaRules.avoid_basic_economy)}</strong></li>
              <li>Prefer cabin minimum: <strong>{deltaRules.prefer_cabin_minimum}</strong></li>
              <li>Track MQD + Pending MQD + Medallion Progress</li>
              <li>Flag refundable fares (executive flexibility)</li>
              <li>Same-Day Confirmed + Same-Day Standby flagged</li>
              <li>TakeOff 15 eligibility (Delta Amex)</li>
              <li>Million Miler progress tracked</li>
            </ul>
          )}
        </Card>

        <Card title="Preferred Airlines" testid="ceo-travel-airlines">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {airlines.map((a) => <span key={a} style={styles.badge("ok")}>{a}</span>)}
          </div>
        </Card>

        <Card title="Africa Travel Command" testid="ceo-travel-africa">
          <div style={styles.cardLine}>Lagos · Accra · Nairobi · Kigali · Entebbe</div>
          <div style={styles.cardDim}>
            US ↔ West Africa: 45–90 day book window. US ↔ East Africa: 60–120 days via AMS / CDG / DXB / DOH.
          </div>
        </Card>

        <Card title="Hotel + Ground Transport Planner" testid="ceo-travel-hotel-ground">
          <StatusBadge status="AWAITING_PROVIDER_KEY" />
          <div style={styles.cardDim}>
            Hotel and ground transport orchestration is awaiting partner provider keys.
            Persist user-entered itineraries to populate downstream planners.
          </div>
        </Card>

        <Card title="Same-Day Change · Standby Advisor" testid="ceo-travel-sdc-standby">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 1.7 }}>
            <li>SDC eligible when same origin/destination on Delta + Medallion tier.</li>
            <li>Standby is free for Medallion; subject to availability.</li>
            <li>Avoid Basic Economy — SDC and standby disallowed.</li>
          </ul>
        </Card>

        <Card title="Flexible Ticket Optimizer" testid="ceo-travel-flex-ticket">
          <div style={styles.cardLine}>
            Prefer refundable Main+ / Delta One on Africa long-hauls.
          </div>
          <div style={styles.cardDim}>
            When schedule risk &gt; 30%, the optimizer recommends the refundable variant.
          </div>
        </Card>
      </div>

      {/* ── Quick Actions (TravelAgent™) ─────────────────────────────────── */}
      <div style={{ marginTop: 36 }}>
        <h3 style={{ ...styles.h2, color: PALETTE.gold }}>Quick Actions · TravelAgent™</h3>
        <div style={{ ...styles.card, marginTop: 12 }} data-testid="ceo-travel-quick-actions">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {quickActions.map((qa) => (
              <button
                key={qa.label}
                type="button"
                style={styles.btn(false)}
                onClick={() => { setAgentInput(qa.q); askAgent(qa.q); }}
                data-testid={`ceo-travel-quick-${qa.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                {qa.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <input
              type="text"
              value={agentInput}
              onChange={(e) => setAgentInput(e.target.value)}
              placeholder="Ask TravelAgent™ (e.g. Build Rwanda itinerary)"
              style={{ flex: 1, minWidth: 280, background: "rgba(0,0,0,0.4)", border: `1px solid ${PALETTE.panelLine}`, color: PALETTE.ink, padding: "10px 12px", borderRadius: 8, fontSize: 13.5 }}
              data-testid="ceo-travel-agent-input"
            />
            <button
              type="button"
              style={styles.btn(true)}
              onClick={() => askAgent(agentInput)}
              data-testid="ceo-travel-agent-ask"
            >
              Ask TravelAgent™
            </button>
          </div>

          {agentReply && (
            <div style={{ marginTop: 14, padding: 12, background: "rgba(0,0,0,0.35)", border: `1px solid ${PALETTE.panelLine}`, borderRadius: 8 }} data-testid="ceo-travel-agent-reply">
              {agentReply.error ? (
                <div style={{ color: PALETTE.alert }}>{agentReply.error}</div>
              ) : (
                <>
                  <div style={styles.cardLine}>
                    <strong style={{ color: PALETTE.gold }}>{agentReply.agent}</strong> ·{" "}
                    <StatusBadge status={agentReply.fare_lookup?.status || "NO_DATA_AVAILABLE"} />
                  </div>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: 20, fontSize: 13.5, lineHeight: 1.65 }}>
                    {(agentReply.suggestions || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                  <div style={styles.cardDim}>{agentReply.fare_lookup?.message}</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── 8AM Briefing preview + 9AM email preview ─────────────────────── */}
      <div style={{ ...styles.grid, marginTop: 36 }}>
        <Card title="8AM CEO Travel Brief" testid="ceo-travel-8am-brief">
          {brief && (
            <pre style={{ margin: 0, color: PALETTE.ink, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
{`CEO Travel Intelligence (${(brief.generated_at || "").slice(0, 16)})
─────────────────────────────────────────
Today's Route Deals    : ${brief.todays_route_deals?.status} (${brief.todays_route_deals?.count} item${brief.todays_route_deals?.count === 1 ? "" : "s"})
Delta MQD Progress     : ${brief.delta_mqd_progress?.current_mqd} (+${brief.delta_mqd_progress?.pending_mqd} pending) → ${brief.delta_mqd_progress?.target_medallion}
Upcoming Itineraries   : ${brief.upcoming_itinerary_risks?.count}
Active Travel Alerts   : ${brief.international_travel_alerts?.count}
Refundable Fare Status : ${brief.refundable_fare_opportunities?.status}
─────────────────────────────────────────
Best Booking Windows:
${(brief.best_booking_windows || []).map((b) => "  · " + b).join("\n")}`}
            </pre>
          )}
        </Card>

        <Card title="9AM CEO Email Travel Summary" testid="ceo-travel-9am-email">
          {emailDraft && (
            <>
              <div style={styles.cardDim}><strong>To:</strong> {emailDraft.to}</div>
              <div style={styles.cardDim}><strong>Subject:</strong> {emailDraft.subject}</div>
              <pre style={{ margin: "8px 0 0 0", color: PALETTE.ink, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{emailDraft.body}</pre>
            </>
          )}
        </Card>
      </div>

      {/* ── Disclaimer ───────────────────────────────────────────────────── */}
      <div style={styles.disclaimer} data-testid="ceo-travel-disclaimer">
        <strong style={{ color: PALETTE.warn }}>Travel disclaimer:</strong>{" "}
        Travel intelligence is informational only. Fares, schedules, visa rules,
        and airline policies can change. Confirm directly with airline, government,
        or booking provider before purchase.
      </div>
    </div>
  );
}

const NO_LIVE_FARE_LABEL =
  "NO_LIVE_FARE_DATA_AVAILABLE — connect Amadeus / Duffel / Skyscanner key to surface live fares.";
